import { projectCategoriesTransformer } from "features/projects/api/transformResponse/projectCategories"
import parentApi from "store/parentApi"
import { arrayBufferToBase64, convertQueryObjToString, localisedValue } from "utils"
import { dateTimeFunction } from "utils/date"
import { billingTypes } from "../data"
import transformResponse from "./transformResponse"
import checkPhoneNumberTransformer from "./transformResponse/checkPhoneNumberTransformer"
import clientCellTransform from "./transformResponse/clientCellTransform"
import conversionProbabilityTransformer from "./transformResponse/conversionProbabilityTransformer"
import designationAndPaymentDiscountTransformer from "./transformResponse/designationAndPaymentDiscountTransformer"
import feedbackOptionsTransformer from "./transformResponse/feedbackOptionsTransformer"
import flatStaffHierarchyTransformer from "./transformResponse/flatStaffHierarchyTransformer"
import getAgencyTransformHelper from "./transformResponse/getAgencyTransformHelper"
import languageTransformer from "./transformResponse/languagesTransformer"
import mediaDataTransformer from "./transformResponse/mediaDataTransformer"
import possessionAndInstalmentTransformer from "./transformResponse/possessionAndInstalmentTransformer"
import recruitmentDriveTransformer from "./transformResponse/recruitmentDriveTransformer"
import { salesEventDetailTransformer } from "./transformResponse/salesEventDetailTransformer"
import staffListTransformer from "./transformResponse/staffListTransformer"
import { unitTypesWithSubTypesTransformer } from "./transformResponse/unitTypeTransformer"
import { leadLabelsTransformer } from "./transformResponse/leadLabelsTransformer"
import { flaggedLeadDetailsTransformer } from "./transformResponse/flaggedLeadDetailsTransformer"
import sharedWithTransformer from "features/leads/api/sharedWithTransformer"
import projectDetailWithMediaDataTransformer from "./transformResponse/projectDetailWithMediaTransformer"
import checkEmailTransformer from "./transformResponse/checkEmailTransformer"
const sharedApis = parentApi.injectEndpoints({
  endpoints: build => ({
    getCountries: build.query({
      query: params => `/countries`,
      transformResponse: (response, meta, arg) => {
        return transformResponse.countries(response, arg?.nationalityFlag)
      },
    }),

    getStates: build.query({
      query: countryId => `/categories?countryId=${countryId}&catLevel=2`,
      transformResponse: response => transformResponse.countries(response),
    }),

    getLocationsByParent: build.query({
      query: ({ parentId, locLevel }) => `/categories?parent=${parentId}&catLevel=${locLevel}`,
      transformResponse: response => transformResponse.countries(response),
    }),

    getCities: build.query({
      query: id => (id ? `categories/getCities?countryId=${id}` : `categories/getCities`),
      transformResponse: response => transformResponse.cities(response),
    }),

    getLocality: build.query({
      query: id => (id ? `/categories/getArea?id=${id}` : `/categories/getArea`),
      transformResponse: response => transformResponse.localities(response),
    }),

    getProjects: build.query({
      query: params => {
        let paramsObj = { ...params }
        let { billingType } = paramsObj
        if (!!billingType) {
          paramsObj["billingTypeKey"] = billingTypes()[billingType]?.key
        }
        delete paramsObj["billingType"]
        return `/definitions/v1/projects?sortBy=name&sortOrder=asc&${convertQueryObjToString(paramsObj)}`
      },
      transformResponse: response => transformResponse.projects(response),
    }),

    getProjectDetail: build.query({
      query: params => `/projects/detail/${params.id}`,
      transformResponse: response => projectDetailWithMediaDataTransformer(response),
    }),

    getProjectBifurcatedUnits: build.query({
      query: params => `/projects/bifurcatedUnits/${params.id}`,
      transformResponse: response => transformResponse.projectBifurcatedUnits(response),
    }),

    getProjectUnits: build.query({
      query: obj => `/listings/getUnits?listingAgainst=project&${convertQueryObjToString({ ...obj })}`,
      transformResponse: response =>
        response.map(e => ({
          ...e,
          key: e?.id,
          label: localisedValue({ object: e, key: "unitNumber" }),
          value: e?.id?.toString(),
        })),
    }),

    getAmenities: build.query({
      query: () => `/amenities`,
    }),

    getRegions: build.query({
      query: params => `/regions`,
      transformResponse: (response, meta, arg) => {
        return arg?.module === "bonusPolicy"
          ? transformResponse.hierarchicalRegions(response)
          : transformResponse.regions(response)
      },
    }),

    getPbxMachines: build.query({
      query: () => `pbxMachines`,
    }),

    getPbxValidator: build.query({
      query: params => `/users/validatePBXExtension?${convertQueryObjToString(params)}`,
    }),

    getOccupation: build.query({
      query: arg => `/occupations`,
      transformResponse: (response, meta, arg) => transformResponse.occupations(response, arg),
    }),

    getClientType: build.query({
      query: arg => (arg?.hierarchical ? `/clientTypes?hierarchical=true` : `/clientTypes`),
      transformResponse: (response, meta, arg) =>
        arg?.hierarchical
          ? transformResponse.hierarchicalClientTypes(response)
          : transformResponse.clientTypes(response),
    }),

    getClientRating: build.query({
      query: () => `/clientRatings`,
      transformResponse: response => transformResponse.clientRatings(response),
    }),

    getClientBuyingIntent: build.query({
      query: () => `/clientBuyingIntents`,
      transformResponse: response => transformResponse.buyingIntents(response),
    }),

    getUsers: build.query({
      query: ({ privileged = true, ...params }) =>
        `/teamMembers${privileged ? "/privilegedUsers" : ""}?${convertQueryObjToString(params)}`,
      transformResponse: (response, meta, arg) => {
        return arg?.module === "staffManagement" && arg?.staffUsers
          ? transformResponse.allStaffMembers(response)
          : arg?.teamType?.length === 1 && arg?.teamType?.[0] === "mortgage"
            ? response
            : transformResponse.staffMembers(response, arg)
      },
    }),

    getUnitsByProject: build.query({
      query: obj =>
        !Object.keys(obj || {}).length
          ? `/listings/getUnits?listingAgainst=lead`
          : obj?.id
            ? `/listings/getUnits?projectIds=${obj?.id}&listingAgainst=project`
            : `/listings/getUnits?listingAgainst=project&${convertQueryObjToString({ ...obj })}`,
      transformResponse: data =>
        data?.map(item => ({
          ...item,
          label: localisedValue({ object: item, key: "unitNumber" }),
          value: item?.id?.toString(),
        })),
    }),

    getStaffHierarchy: build.query({
      query: ({ userId, flatListTransformation, ...params }) =>
        `/users/reportees/${userId}?hierarchy=true&${convertQueryObjToString({ ...params })}`,
      transformResponse: (response, meta, arg) => {
        const { flatListTransformation, requireSelfSellerData, getStaffValueObjectMapping } = arg
        return staffListTransformer(response, flatListTransformation, requireSelfSellerData, getStaffValueObjectMapping)
      },
    }),

    getFlatStaffHierarchy: build.query({
      query: ({ userId, ...params }) =>
        `/users/reportees/${userId}?hierarchy=false&${convertQueryObjToString({ ...params })}`,
      transformResponse: response => flatStaffHierarchyTransformer(response),
    }),

    getFilterTags: build.query({
      query: params => `/appliedFilters?${convertQueryObjToString(params)}`,
    }),

    getCheckEmail: build.query({
      query: params => {
        const payload = {
          value: params?.email,
          ...(params?.id && { exceptField: "id", exceptValue: params.id }),
          ...(params?.matchedDetails !== undefined && { matchedDetails: params.matchedDetails }),
          ...(params?.reAllocationCheck !== undefined && { reAllocationCheck: params.reAllocationCheck }),
        }
        return `/services/checkMatch/Client/email?${convertQueryObjToString(payload)}`
      },
      transformResponse: response => checkEmailTransformer(response),
    }),

    getPaymentModes: build.query({
      query: () => `/paymentModes`,
      transformResponse: data => data?.map(item => ({ ...item, label: item?.name, value: item?.id?.toString() })),
    }),
    getPaymentTypes: build.query({
      query: () => `paymentTypes?active=true`,
      transformResponse: data => data?.map(item => ({ ...item, label: item?.name, value: item?.id?.toString() })),
    }),

    getCheckPhoneNumber: build.query({
      query: params => {
        const { cell, clientId, reAllocationCheck, ...rest } = params
        const payload = {
          ...rest,
          value: cell?.slice(-9),
          cellNumber: cell.replace("+", ""),
          matchedDetails: true,
          ...(reAllocationCheck && { reAllocationCheck }),
          ...(clientId && { id: clientId }),
        }
        return `services/checkMatch/ClientCell/short?${convertQueryObjToString(payload)}`
      },
      transformResponse: response => checkPhoneNumberTransformer(response),
    }),

    getCheckNationalId: build.query({
      query: params =>
        `/services/checkMatch/Client/nationalId?value=${params.nationalId}&exceptField=id&exceptValue=${params.id}`,
    }),

    getTypes: build.query({
      query: () => `/types/typesWithChildren`,
    }),

    getSource: build.query({
      query: () => `/internalInquirySources/sourcesWithChildren?internalActive=true`,
      transformResponse: response =>
        response.map(item => ({
          ...item,
          label: item?.name,
          value: item?.id,
        })),
    }),

    getClassification: build.query({
      query: () => `internalInquiryClassifications`,
      transformResponse: response => transformResponse.classification(response),
    }),

    getDynamicFilters: build.query({
      query: params => `dynamicFilters/search_client?${convertQueryObjToString(params)}`,
    }),

    getTimeZones: build.query({
      query: () => `/definitions/timezones`,
    }),

    getLanguages: build.query({
      query: () => `/definitions/languages`,
      transformResponse: response => languageTransformer(response),
    }),

    getStaffDetails: build.query({
      query: userId => `/users/basicDetails/${userId}`,
    }),

    getS3Presigned: build.query({
      query: ({ path }) => path,
      transformResponse: response => ({ ...response, url: response.signedUrl || response.url }),
    }),

    updateS3: build.mutation({
      query: params => {
        return {
          url: params.url,
          method: "PUT",
          body: params.file,
          headers: { "Content-Type": "application/octet-stream" },
        }
      },
    }),
    changeLanguage: build.mutation({
      query: id => {
        return {
          url: "/users/language",
          method: "PUT",
          body: { languageId: id },
        }
      },
    }),

    getTeamTypes: build.query({
      query: params => `/TeamTypes?${convertQueryObjToString(params)}`,
      transformResponse: (response, _meta, args) => transformResponse.teamTypeTransformer(response, args),
    }),

    getSelectedDepartments: build.query({
      query: params => `/teamtypes?${convertQueryObjToString(params)}`,
      transformResponse: (response, _meta, args) => transformResponse.teamTypeTransformer(response, args),
    }),

    getClientBasicDetails: build.query({
      query: clientId => `/clients/basicDetails/${clientId}`,
    }),

    getCheckReferralToClient: build.query({
      query: params => `/affiliates/referralToClient?${convertQueryObjToString(params)}`,
    }),
    getAffiliateLeadsType: build.query({
      query: () => `/affiliateTypes`,
      transformResponse: response => response?.map(item => ({ ...item, value: item.id.toString() })),
    }),

    getQrCodeLogin: build.query({
      query: params => `/authentication/qrCode/${params?.userId}`,
    }),

    getGAQrCodeLogin: build.query({
      query: params => `/authentication/qrCode/${params?.userId}/gAuth`,
    }),

    getCurrencies: build.query({
      query: () => `/definitions/currencies`,
      transformResponse: response => response.map(e => ({ ...e, label: e.currencyCode, value: e.id.toString() })),
    }),
    getAgencyCurrency: build.query({
      query: () => `/agency/currencies`,
      transformResponse: response =>
        response.map((e, index) => ({ ...e, title: e.currencyCode, value: e?.value, key: index })),
    }),
    getAgency: build.query({
      query: () => `/agency`,
      transformResponse: response => getAgencyTransformHelper(response),
    }),

    editAgency: build.mutation({
      query: agencyInfo => {
        return { url: `/agency`, method: "PUT", body: agencyInfo }
      },
    }),

    getUserDesignation: build.query({
      query: params => `/userDesignations?${convertQueryObjToString(params)}`,
      transformResponse: response => response?.map(item => ({ ...item, value: item.id.toString() })),
    }),

    getTaskType: build.query({
      query: ({ params }) => {
        let updatedParams = { ...params }
        if (updatedParams?.from) delete updatedParams?.from

        return `/internalInquiryStatuses?${convertQueryObjToString(updatedParams)}`
      },
      transformResponse: (response, _meta, args) => transformResponse.taskTypes(response, args),
    }),

    getCalenderCities: build.query({
      query: () => `/categories/getCities?catLevel=3`,
      transformResponse: response =>
        response.map(e => ({ ...e, value: e.id + "", label: localisedValue({ object: e, key: "name" }) })),
    }),
    getUnitNumber: build.query({
      query: obj => {
        return `/listings/getUnits?listingAgainst=project&${convertQueryObjToString({ ...obj })}`
      },
      transformResponse: res => res.map(e => ({ ...e, id: e.id, label: e.unitNumber, value: e.id.toString() })),
    }),
    getInventoryProject: build.query({
      query: obj => `/projects/privilegedProjects?${convertQueryObjToString({ ...obj })}`,
      transformResponse: res => transformResponse.inventoryProjectTransformer(res),
    }),

    getAreaUnits: build.query({
      query: () => `/areaUnits`,
      transformResponse: res => res.map(e => ({ ...e, value: e.shortName, label: e.displayText })),
    }),

    getUnitTypes: build.query({
      query: ({ isFlatHierarchy, ...obj }) => {
        return `/types/v1/typesWithChildren?${convertQueryObjToString({ ...obj })}`
      },
      transformResponse: (res, _, args) => unitTypesWithSubTypesTransformer(res, args),
    }),

    getPurpose: build.query({
      query: () => `/purposes`,
    }),
    getPossessionAndInstalmentInfo: build.query({
      query: ({
        projectId,
        listingId,
        dealPrice,
      }) => `/projects/${projectId}/bookingInfo?listingId=${listingId}&dealPrice=${dealPrice}
      `,
      transformResponse: data => possessionAndInstalmentTransformer(data),
    }),
    getActivityLog: build.query({
      query: params => {
        if (!params?.id || !params?.module) {
          return null
        }
        return `/logs?recordId=${params?.id}&record=${params?.module}`
      },
    }),
    getAttachmentFile: build.query({
      query: id => ({
        url: `/payments/v2/${id}`,
        responseHandler: response => (response.ok ? response.arrayBuffer() : response.json()), //convert file stream to array buffer
        headers: { accept: "application/pdf" },
      }),
      transformResponse: data => arrayBufferToBase64(data), //convert file from array buffer to base64
    }),
    getClientCells: build.query({
      query: params => `/clients/${params?.clientId}/cells?action=${params.actionType}`,
      transformResponse: (response, req, params) => {
        return clientCellTransform(response, req, params)
      },
    }),
    getExternalClientCells: build.query({
      query: params => `/externalInquiries/${params?.clientId}/cells?action=${params.actionType}`,
    }),

    getCallNumber: build.query({
      query: params =>
        `/clients/${params?.clientId}/cells?action=${params.actionType}&phoneCallId=${params?.phoneCallId}`,
    }),
    getProfileSummary: build.query({
      query: ({ staffId, load, params }) => `/users/${staffId}/summary?load=${load}&${convertQueryObjToString(params)}`,
    }),

    performPbxCall: build.mutation({
      query: data => {
        return { url: `/pbxMachines/makePBXCall`, method: "POST", body: data }
      },
    }),

    addReservationQueue: build.mutation({
      query: data => {
        return { url: `/listingReservationQueue`, method: "POST", body: data }
      },
      invalidatesTags: ["inventory-listing"],
    }),
    leadValidationForReservationQueue: build.query({
      query: ({ leadId }) => `/listingReservationQueue/${leadId}/leadValidation`,
    }),
    deleteReservationQueue: build.mutation({
      query: ({ queueId }) => {
        return { url: `/listingReservationQueue/${queueId}`, method: "DELETE" }
      },
      invalidatesTags: ["inventory-listing"],
    }),
    getReservationLogs: build.query({
      query: ({ ...params }) => {
        let obj = { ...params }
        return `/listingReservationLogs?${convertQueryObjToString({ ...obj })}`
      },
    }),
    generateProposal: build.mutation({
      query: ({
        payload,
        inquiryId,
        listingId,
        floorPlan,
        preview,
        isCustom,
        customPaymentPlan,
        selectPaymentPlan,
      }) => {
        const floorPlans = []

        if (floorPlan?.fileList && floorPlan?.fileList?.length > 0) {
          floorPlans.push(
            ...floorPlan?.fileList.map(item => ({
              name: item?.name,
              file: item?.thumbUrl?.split(",")[1],
            }))
          )
        }

        if (payload?.selectedFloorPlans) {
          floorPlans.push(
            ...payload.selectedFloorPlans.map(item => ({
              name: item?.name,
              s3Key: item?.s3Key,
            }))
          )
        }

        let body = {
          projectId: payload?.projects?.projectId,
          listingId: listingId,
          preview: preview,
          paymentDetails: {
            discountPercentage: payload?.discountPercentage,
            bookingPrice: payload?.bookingPrice,
            cdpPercentage: payload?.cdp,
            possessionAmount: payload?.possessionAmount,
            selectedFeatureIds: payload?.featureTable?.selectedRowKeys,
          },
          ...(selectPaymentPlan && {
            paymentPlan: {
              instalmentDuration: payload?.months || customPaymentPlan?.length,
              ...(payload?.instalmentType == "time_based" && {
                instalmentFrequency: payload?.installmentFrequency,
                startDate: dateTimeFunction({ date: payload?.startDate, apiRequest: true, timeZone: true }),
              }),
              includeBalloonPayments: payload?.addBalloonPayment, // Optional. If balloon payments are selected
              totalballoonPaymentAmount: payload?.balloonPaymentAmount?.value, // Required if includeBalloonPayments = true. Total balloon payment amount
              balloonPaymentFrequency: payload?.balloonFrequency, // Required if includeBalloonPayments = true. `quarterly, bi_annually, annually`
              isCustomPaymentPlan: isCustom, // Optional. If custom payment plan is selected
              ...(isCustom && {
                customPaymentPlan:
                  payload?.instalmenntType == "time_based"
                    ? customPaymentPlan?.map((obj, index) => ({
                        index: index + 1,
                        type: obj?.type, // Can be `installment or balloon_payment`
                        date: dateTimeFunction({ date: obj?.date, timeZone: true, apiRequest: true }),
                        amount: obj?.amount,
                      }))
                    : customPaymentPlan?.map(item => ({ ...item, amount: +item?.amount })),
              }),
              ...(payload?.startDate && {
                startDate: dateTimeFunction({ date: payload?.startDate, apiRequest: true, timeZone: true }),
              }),
            },
          }),
          settings: {
            showClientName: payload?.proposalSettings?.includes("showClientName"),
            showUnitNetArea: payload?.proposalSettings?.includes("showUnitNetArea"),
            showUnitGrossArea: payload?.proposalSettings?.includes("showUnitGrossArea"),
            showPossessionAmount: payload?.proposalSettings?.includes("showPossessionAmount"),
            generateBookingForm: payload?.proposalSettings?.includes("generateBookingForm"),
            showUserBusinessCard: payload?.proposalSettings?.includes("includeBusinessCard"),
          },
          source: "web_v2",
          floorPlans: floorPlans,
          projectPhotos: payload?.selectedProjectPhotos?.map(item => ({ name: item?.name, s3Key: item?.s3Key })),
          ...(payload?.selectedCoverPhotos?.[0]?.s3Key && {
            coverPhoto: {
              name: payload?.selectedCoverPhotos?.[0]?.name,
              s3Key: payload?.selectedCoverPhotos?.[0]?.s3Key,
            },
          }),
          isInstalmentsRequired: payload?.paymentPlanCheckbox,
          instalmentType: payload?.instalmentType,
          hasAdjustableRent: false,
        }
        return {
          url: `/internalInquiries/${inquiryId}/generateProposal`,
          method: "POST",
          body: body,
          responseHandler: response => (response.ok ? response.arrayBuffer() : response.json()),
          headers: { accept: "application/pdf" },
        }
      },
    }),
    getDesignationDiscounts: build.query({
      query: params => `/projects/${params.projectId}/designationDiscounts?listingId=${params.listingId}`,
    }),
    getDesignationAndPaymentDiscounts: build.query({
      query: params => `/projects/${params.projectId}/discounts?listingId=${params.listingId}`,
      transformResponse: data => designationAndPaymentDiscountTransformer(data),
    }),
    getClientTaskLogs: build.query({
      query: id => `/clients/${id}/taskLog?withInquiries=true`,
      transformResponse: data => data?.leads.map(lead => lead.id),
    }),
    allocateStaff: build.mutation({
      query: ({ projectId, ...values }) => {
        return {
          url: `/projects/${projectId}/updateAllocatedUsers`,
          method: "PUT",
          body: values,
        }
      },
      invalidatesTags: (resp, err) => (resp ? ["staff", "projects-listing"] : []),
    }),
    getRecruitmentDrive: build.query({
      query: () => `/userLabels`,
      transformResponse: response => recruitmentDriveTransformer(response),
      providesTags: ["recruitment-drive"],
    }),
    verifyDiscountStatus: build.query({
      query: data => {
        return { url: `/dealDiscounts/slug/${data?.slug}?response=${data?.status}` }
      },
    }),
    getAffiliateClassifications: build.query({
      query: () => `/affiliateClassifications`,
      transformResponse: response => transformResponse.classification(response),
    }),
    getBillingTypes: build.query({
      query: () => `/billingtypes`,
      transformResponse: data => projectCategoriesTransformer(data),
    }),
    getClientProposals: build.query({
      query: ({ id, params }) => `/clients/${id}/proposalLogs?${convertQueryObjToString(params)}`,
    }),
    getLeadProposals: build.query({
      query: ({ id, params }) => `/internalInquiries/${id}/proposalLogs?${convertQueryObjToString(params)}`,
    }),
    getFeatureFeedbackOptions: build.query({
      query: params => `/userFeatureFeedbacks/options?${convertQueryObjToString(params)}`,
      transformResponse: response => feedbackOptionsTransformer(response),
    }),
    addFeatureFeedback: build.mutation({
      query: data => {
        return { url: `/userFeatureFeedbacks`, method: "POST", body: data }
      },
    }),
    getAllocatedUserDetails: build.query({
      query: projectId => `/projects/${projectId}/cityWiseUsers`,
      transformResponse: response => transformResponse.userAllocations(response),
    }),

    getStaffUserDesignations: build.query({
      query: params => `/userDesignations${params?.teamTypeId ? `/${params?.teamTypeId}/teamTypeWise` : ""}`,
    }),
    getPhoneCallsByTag: build.query({
      query: ({ id, params }) => `/phoneCalls/callsByTag?clientId=${id}&${convertQueryObjToString(params)}`,
    }),
    getSharingPurposes: build.query({
      query: params => `/internalInquirySharings/purposes?${convertQueryObjToString(params)}`,
      transformResponse: response => transformResponse.leadSharingPurposesTransformer(response),
    }),
    getSharedWith: build.query({
      query: ({ userId, flatListTransformation, ...params }) =>
        `/users/reportees/${userId}?hierarchy=true&${convertQueryObjToString({ ...params })}`,
      transformResponse: (response, meta, arg) => {
        const { flatListTransformation, requireSelfSellerData } = arg
        return staffListTransformer(response, flatListTransformation, requireSelfSellerData)
      },
    }),
    getSharedStaff: build.query({
      query: params => `/teamMembers/privilegedUsers?${convertQueryObjToString(params)}`,
      transformResponse: data => {
        return sharedWithTransformer(data)
      },
    }),
    getUploadId: build.query({
      query: data => `/attachments/uploadId?fileName=${data?.fileName}&fileType=${data?.fileType}`,
      extraOptions: { maxRetries: 5 }, // Only this endpoint retries
    }),
    getPresignedUrls: build.query({
      query: data => `/attachments/writePresignedUrl?${convertQueryObjToString(data)}`,
      extraOptions: { maxRetries: 5 }, // Only this endpoint retries
      transformResponse: data => {
        return data?.presignedUrls?.reduce(
          (initialObject, value) => ({ ...initialObject, [value?.serialNumber]: value?.signedUrl }),
          {}
        )
      },
    }),
    getLeadResharingReasons: build.query({
      query: () => `/leadresharingreasons`,
      transformResponse: response => transformResponse.leadResharingReasons(response),
    }),
    postOnPresigned: build.mutation({
      query: ({ query, data, signal }) => {
        return {
          url: query,
          body: data,
          method: "PUT",
          headers: { "Content-Type": "application/octet-stream" },
          signal,
        }
      },
      extraOptions: { maxRetries: 5 }, // Only this endpoint retries
      transformResponse: (response, meta) => {
        return { etag: meta?.response?.headers.get("ETag") }
      },
    }),
    getSalesEventById: build.query({
      query: params => `/calendarEvents/salesEvent/${params?.id}`,
      transformResponse: (response, meta, arg) => (arg?.transform ? salesEventDetailTransformer(response) : response),
    }),
    getPartners: build.query({
      query: params => `/externalInquiryPartners?${convertQueryObjToString(params)}`,
      transformResponse: response => {
        return {
          ...response,
          rows: response.rows.map(row => ({
            ...row,
            value: row?.id?.toString(),
            label: localisedValue({ object: row, key: "name" }),
          })),
        }
      },
      providesTags: ["lms-partners"],
    }),
    getConversionProbability: build.query({
      query: params =>
        `clients/${params.clientId}/clientAnalysis?requestType=client_classification&month=${
          params.month + 1
        }&inquiryId=${params.inquiryId}`,
      transformResponse: (response, _meta, args) => conversionProbabilityTransformer(response, args.month),
    }),
    getClientLeads: build.query({
      query: params => `internalInquiries/search?clientId=${params?.clientId}`,
      transformResponse: resp => resp?.data?.map(e => ({ label: e.id, value: e.id })),
    }),
    getLowPerformanceWarningDocument: build.query({
      query: ({ useBuffer, ...params }) => ({
        url: `/user-signoff/lowPerformanceWarning?${convertQueryObjToString({ ...params })}`,
        responseHandler: response => (response.ok ? response.arrayBuffer() : response.json()),
        headers: { accept: "application/pdf" },
      }),
      transformResponse: (data, _meta, arg) => (arg.useBuffer ? data : arrayBufferToBase64(data)),
    }),
    signLowPerformanceWarningDocument: build.mutation({
      query: ({ userId, ...values }) => {
        return {
          url: `/users/${userId}/signTerminationWarning`,
          method: "PUT",
          body: values,
        }
      },
    }),
    getFlaggedLeadDetails: build.query({
      query: params => `/internalInquiryCategories/${params?.inquiryId}?${convertQueryObjToString(params)}`,
      transformResponse: response => flaggedLeadDetailsTransformer(response),
    }),
    getProjectsByCity: build.query({
      query: id => `/definitions/v1/projects?sortBy=name&sortOrder=asc&status=on&cityIds=${id}`,
      transformResponse: resp =>
        resp?.map(e => ({
          ...e,
          label: localisedValue({ object: e, key: "name" }),
          value: e?.projectId,
          id: e?.projectId,
        })),
    }),
    callViaGrandStream: build.mutation({
      query: values => {
        return {
          url: `phonecalls/callViaGrandStream`,
          method: "POST",
          body: values,
        }
      },
    }),
    getClientIBAN: build.query({
      query: ({ clientId }) => {
        return `clients/${clientId}/iban`
      },
      providesTags: (result, error, { clientId }) => [{ type: "client-iban", id: clientId }],
    }),
    addClientIBAN: build.mutation({
      query: ({ body, clientId }) => {
        return {
          url: `clients/${clientId}/iban`,
          method: "PUT",
          body: body,
        }
      },
      invalidatesTags: (result, error, { clientId }) => (!error ? [{ type: "client-iban", id: clientId }] : []),
    }),
    createVIBANForContract: build.mutation({
      query: payload => {
        return {
          url: `escrows/create-viban`,
          method: "POST",
          body: payload,
        }
      },
      invalidatesTags: (result, error, { contractId }) => (!error ? [{ type: "viban", id: contractId }] : []),
    }),
    getVIBANForContract: build.query({
      query: ({ contractId }) => `escrows/contract/${contractId}/config`,
      providesTags: (result, error, { contractId }) => [{ type: "viban", id: contractId }],
    }),
    getNationalities: build.query({
      query: () => `/countries`,
      transformResponse: response =>
        response?.map(e => ({
          ...e,
          label: localisedValue({ object: e, key: "nationalityTitle" }),
          value: e?.id?.toString(),
        })),
    }),
    getMeetingPushedStages: build.query({
      query: module => `meetingPushedStages?module=${module}`,
      transformResponse: response =>
        response?.map(e => ({
          ...e,
          label: localisedValue({ object: e, key: "title" }),
          value: e?.id?.toString(),
        })),
    }),
    getSaleEventsByDate: build.query({
      query: ({ date }) => `/CalendarEvents/salesEvent/byDate?checkDate=${date}`,
      transformResponse: response =>
        response?.data?.map(e => ({
          ...e,
          label: localisedValue({ object: e, key: "name" }),
          value: e?.id,
        })),
    }),
    overrideExisting: false,
  }),
})

export const {
  useLazyGetLeadResharingReasonsQuery,
  useLazyGetAllocatedUserDetailsQuery,
  useGetCountriesQuery,
  useLazyGetCountriesQuery,
  useLazyGetTimeZonesQuery,
  useLazyGetLanguagesQuery,
  useLazyGetActivityLogQuery,
  useGetActivityLogQuery,
  useLazyGetCheckReferralToClientQuery,
  useGetCitiesQuery,
  useLazyGetCitiesQuery,
  useGetLocationsByParentQuery,
  useLazyGetLocationsByParentQuery,
  useGetStatesQuery,
  useLazyGetStatesQuery,
  useLazyGetLocalityQuery,
  useGetLocalityQuery,
  useGetAmenitiesQuery,
  useLazyGetCheckEmailQuery,
  useLazyGetCheckPhoneNumberQuery,
  useLazyGetCheckNationalIdQuery,
  useLazyGetPbxValidatorQuery,
  useGetOccupationQuery,
  useLazyGetOccupationQuery,
  useGetClientTypeQuery,
  useLazyGetClientTypeQuery,
  useGetClientRatingQuery,
  useLazyGetClientRatingQuery,
  useGetClientBuyingIntentQuery,
  useLazyGetClientBuyingIntentQuery,
  useLazyGetUsersQuery,
  useGetUsersQuery,
  useGetProjectsQuery,
  useGetPbxMachinesQuery,
  useLazyGetRegionsQuery,
  useGetRegionsQuery,
  useLazyGetProjectsQuery,
  useGetSourceQuery,
  useGetTypesQuery,
  useLazyGetTypesQuery,
  useGetClassificationQuery,
  useLazyGetClassificationQuery,
  useLazyGetSourceQuery,
  useGetDynamicFiltersQuery,
  useGetProjectBifurcatedUnitsQuery,
  useLazyGetProjectBifurcatedUnitsQuery,
  useLazyGetFilterTagsQuery,
  useGetDisputesQuery,
  useLazyGetDisputesQuery,
  useUpdateS3Mutation,
  useLazyGetS3PresignedQuery,
  useLazyGetStaffDetailsQuery,
  useGetTeamTypesQuery,
  useLazyGetTeamTypesQuery,
  useGetTimeZonesQuery,
  useGetLanguagesQuery,
  useLazyGetClientBasicDetailsQuery,
  useLazyGetQrCodeLoginQuery,
  useLazyGetGAQrCodeLoginQuery,
  useLazyGetAffiliateLeadsTypeQuery,
  useGetCurrenciesQuery,
  useLazyGetCurrenciesQuery,
  useGetAgencyQuery,
  useLazyGetAgencyQuery,
  useEditAgencyMutation,
  useLazyGetUserDesignationQuery,
  useGetUserDesignationQuery,
  useLazyGetTaskTypeQuery,
  useLazyGetCalenderCitiesQuery,
  useLazyGetUnitNumberQuery,
  useGetUnitNumberQuery,
  useLazyGetInventoryProjectQuery,
  useLazyGetProjectUnitsQuery,
  useLazyGetAreaUnitsQuery,
  useLazyGetUnitTypesQuery,
  useLazyGetPurposeQuery,
  useGetPurposeQuery,
  useGetAreaUnitsQuery,
  useGetAgencyCurrencyQuery,
  useLazyGetProjectDetailQuery,
  useLazyGetAttachmentFileQuery,
  useGetPaymentModesQuery,
  useGetPaymentTypesQuery,
  useGetStaffHierarchyQuery,
  useLazyGetStaffHierarchyQuery,
  useLazyGetFlatStaffHierarchyQuery,
  useLazyGetUnitsByProjectQuery,
  useLazyGetProfileSummaryQuery,
  useLazyGetClientCellsQuery,
  useLazyGetExternalClientCellsQuery,
  useLazyGetCallNumberQuery,
  useChangeLanguageMutation,
  useLazyGetPossessionAndInstalmentInfoQuery,
  useAddReservationQueueMutation,
  useLazyLeadValidationForReservationQueueQuery,
  useDeleteReservationQueueMutation,
  useLazyGetReservationLogsQuery,
  useGenerateProposalMutation,
  usePerformPbxCallMutation,
  useLazyGetDesignationDiscountsQuery,
  useLazyGetDesignationAndPaymentDiscountsQuery,
  useLazyGetClientTaskLogsQuery,
  useAllocateStaffMutation,
  useGetRecruitmentDriveQuery,
  useVerifyDiscountStatusQuery,
  useGetAffiliateClassificationsQuery,
  useLazyGetAffiliateClassificationsQuery,
  useGetSelectedDepartmentsQuery,
  useLazyGetSelectedDepartmentsQuery,
  useGetStaffUserDesignationsQuery,
  useLazyGetStaffUserDesignationsQuery,
  useGetBillingTypesQuery,
  useGetClientProposalsQuery,
  useLazyGetClientProposalsQuery,
  useGetLeadProposalsQuery,
  useLazyGetLeadProposalsQuery,
  useGetFeatureFeedbackOptionsQuery,
  useLazyGetFeatureFeedbackOptionsQuery,
  useAddFeatureFeedbackMutation,
  useUploadS3FileInBucketMutation,
  useLazyGetPhoneCallsByTagQuery,
  useGetSharingPurposesQuery,
  useLazyGetSharingPurposesQuery,
  useGetSharedWithQuery,
  useLazyGetSharedStaffQuery,
  useGetSharedStaffQuery,
  useLazyGetSharedWithQuery,
  useLazyGetUploadIdQuery,
  useLazyGetPresignedUrlsQuery,
  usePostOnPresignedMutation,
  useGetSalesEventByIdQuery,
  useLazyGetSalesEventByIdQuery,
  useGetPartnersQuery,
  useLazyGetPartnersQuery,
  useLazyGetConversionProbabilityQuery,
  useGetClientLeadsQuery,
  useGetLowPerformanceWarningDocumentQuery,
  useLazyGetLowPerformanceWarningDocumentQuery,
  useSignLowPerformanceWarningDocumentMutation,
  useLazyGetFlaggedLeadDetailsQuery,
  useLazyGetProjectsByCityQuery,
  useGetProjectsByCityQuery,
  useCallViaGrandStreamMutation,
  useLazyGetClientIBANQuery,
  useCreateVIBANForContractMutation,
  useGetVIBANForContractQuery,
  useLazyGetVIBANForContractQuery,
  useGetClientIBANQuery,
  useAddClientIBANMutation,
  useGetNationalitiesQuery,
  useGetMeetingPushedStagesQuery,
  useLazyGetMeetingPushedStagesQuery,
  useLazyGetSaleEventsByDateQuery,
} = sharedApis
